import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Loader2, Phone, ChevronRight, Volume2 } from "lucide-react";
import axios from "axios";
import { KioskViewport, useKioskSpeech } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const API = '/api';
const IDLE_TIMEOUT = 60;
const LOOKUP_MESSAGE = "Enter your vehicle registration number to check your designated parking status or view your current session. If you have visited participating local retailers, any eligible discounts will be shown automatically.";

// Virtual Keyboard Component - Draggable for 65-inch kiosk
function VirtualKeyboard({ value, onChange, onEnter }) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const keyboardRef = useRef(null);

  const keys = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'DEL']
  ];

  const handleKey = (key) => {
    if (key === 'DEL') onChange(value.slice(0, -1));
    else onChange(value + key);
  };

  const handleDragStart = (e) => {
    if (e.target.closest('[data-drag-handle]')) {
      setIsDragging(true);
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      dragStartRef.current = { x: clientX - position.x, y: clientY - position.y };
      e.preventDefault();
    }
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setPosition({
      x: clientX - dragStartRef.current.x,
      y: clientY - dragStartRef.current.y
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  return (
    <div 
      ref={keyboardRef}
      style={{ 
        transform: `translate(${position.x}px, ${position.y}px)`,
        touchAction: 'none',
        background: '#1e293b',
        borderRadius: '24px',
        padding: '20px 24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
    >
      {/* Draggable Handle */}
      <div 
        data-drag-handle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '16px',
          padding: '12px',
          cursor: 'grab',
          background: '#334155',
          borderRadius: '12px'
        }}
      >
        <span style={{ color: '#94a3b8', fontSize: '18px', fontWeight: '500' }}>☰ Drag to move keyboard</span>
      </div>

      {keys.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
          {row.map((key) => (
            <button
              key={key}
              onClick={() => handleKey(key)}
              style={{
                padding: '20px 24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '24px',
                fontWeight: '700',
                color: 'white',
                background: key === 'DEL' ? '#ef4444' : '#475569',
                minWidth: '60px',
                transition: 'all 0.1s'
              }}
              data-testid={`key-${key}`}
            >
              {key === 'DEL' ? '⌫' : key}
            </button>
          ))}
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
        <button
          onClick={onEnter}
          style={{
            padding: '20px 80px',
            borderRadius: '12px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '24px',
            fontWeight: '700',
            color: 'white',
            background: '#22c55e'
          }}
          data-testid="key-enter"
        >
          SEARCH
        </button>
      </div>
    </div>
  );
}

export default function KioskLookup() {
  const navigate = useNavigate();
  const [vehicleReg, setVehicleReg] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const idleTimerRef = useRef(null);
  
  // Central speech controller - speak lookup guidance
  const { needsInteraction, speak: triggerSpeak } = useKioskSpeech('KioskLookup', LOOKUP_MESSAGE, true);

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  const handleLookup = async () => {
    if (!vehicleReg.trim()) {
      setError("Please enter your vehicle registration");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/lookup/${encodeURIComponent(vehicleReg.trim())}`);
      navigate("/kiosk/status", { state: { lookupResult: response.data, vehicleReg: vehicleReg.trim().toUpperCase() } });
    } catch (err) {
      setError("Unable to find registration. Please check and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-lookup"
        style={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
          overflow: 'hidden',
          background: '#ffffff'
        }}
      >
        {/* STANDARDIZED HEADER */}
        <KioskHeader showDateTime={true} />

        {/* BACK BUTTON - TOP OF CONTENT */}
        <div style={{ padding: '20px 40px', background: 'white' }}>
          <button 
            onClick={() => navigate("/kiosk")}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              background: '#1e3a5f',
              border: 'none',
              cursor: 'pointer'
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={28} color="white" />
            <span style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>Back</span>
          </button>
        </div>

        {/* HERO SECTION */}
        <div style={{
          position: 'relative',
          height: '500px',
          overflow: 'hidden',
          flexShrink: 0
        }}>
          {/* Cityscape Background */}
          <img 
            src="/cityscape.png" 
            alt="Ayr Town Centre"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center bottom'
            }}
          />

          {/* Tracy - Large */}
          <img 
            src="/tracy.png" 
            alt="Tracy - ParkWise Assistant"
            style={{
              position: 'absolute',
              right: '-20px',
              bottom: '-40px',
              height: '580px',
              zIndex: 10
            }}
          />

          {/* Large Speech Bubble */}
          <div style={{
            position: 'absolute',
            top: '30px',
            left: '40px',
            width: '480px',
            background: '#fef9e7',
            borderRadius: '24px',
            padding: '28px 32px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            zIndex: 20
          }}>
            <h3 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: '#1e3a5f',
              margin: '0 0 16px 0'
            }}>
              Check your registration
            </h3>
            <p style={{
              fontSize: '20px',
              color: '#374151',
              lineHeight: 1.5,
              margin: 0
            }}>
              Enter your vehicle registration number to check your designated parking status or view your current session. Local Connect discounts are recognised automatically.
            </p>
            {/* Pointer */}
            <div style={{
              position: 'absolute',
              right: '-22px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '18px solid transparent',
              borderBottom: '18px solid transparent',
              borderLeft: '24px solid #fef9e7'
            }} />
          </div>
        </div>

        {/* INPUT + KEYBOARD SECTION */}
        <div style={{
          flex: 1,
          padding: '30px 40px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #e0f2fe 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px'
        }}>
          {/* VRM Input Display */}
          <div style={{
            width: '600px',
            background: '#fef08a',
            borderRadius: '16px',
            padding: '24px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            border: '4px solid #eab308'
          }}>
            <div style={{
              background: '#1e40af',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '24px',
              fontWeight: '700'
            }}>GB</div>
            <span style={{
              flex: 1,
              fontSize: '48px',
              fontWeight: '700',
              color: '#1e293b',
              letterSpacing: '8px',
              textAlign: 'center',
              fontFamily: 'monospace'
            }}>
              {vehicleReg || 'ENTER REG'}
            </span>
          </div>

          {error && (
            <div style={{
              background: '#fef2f2',
              border: '2px solid #ef4444',
              borderRadius: '12px',
              padding: '16px 24px',
              color: '#dc2626',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Loader2 size={48} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '24px', color: '#1e3a5f' }}>Searching...</span>
            </div>
          ) : (
            <VirtualKeyboard
              value={vehicleReg}
              onChange={(val) => { setVehicleReg(val.toUpperCase()); setError(null); }}
              onEnter={handleLookup}
            />
          )}
        </div>

        {/* HELP INFO - In content area, no footer */}
        <div style={{
          margin: '24px 40px',
          padding: '20px 32px',
          background: '#1e3a5f',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <Phone size={24} color="#fbbf24" />
          <span style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>Need help? Call </span>
          <span style={{ color: '#fbbf24', fontSize: '22px', fontWeight: '700' }}>01292 833107</span>
        </div>
      </div>
    </KioskViewport>
  );
}
